import React, { useMemo, useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  Image,
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { RootStackParamList } from '../types/navigation';
import { createIssueReport } from '../utils/issueReportStorage';
import { getUserProductById } from '../utils/userProductStorage';
import { UserProductWithDetails } from '../types/newDatabaseTypes';
import { IssueType, ISSUE_TYPE_LABELS, ReportPriority } from '../types/issueReport';
import { getFirstImageUrl } from '../utils/imageUtils';
import { useTheme } from '../styles/ThemeProvider';

type Props = NativeStackScreenProps<RootStackParamList, 'ReportIssue'>;

export default function ReportIssueScreen({ route, navigation }: Props) {
  const theme = useTheme();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          flex: 1,
          backgroundColor: theme.colors.backgroundPrimary,
        },
        content: {
          padding: theme.spacing.xl,
        },
        centered: {
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.backgroundPrimary,
        },
        title: {
          ...theme.typography.h3,
          color: theme.colors.textPrimary,
          marginBottom: theme.spacing.xl,
        },
        productPreview: {
          flexDirection: 'row',
          padding: theme.spacing.md,
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          marginBottom: theme.spacing.xl,
        },
        productImage: {
          width: 80,
          height: 80,
          borderRadius: theme.radius.radiusSm,
          marginRight: theme.spacing.md,
        },
        productInfo: {
          flex: 1,
          justifyContent: 'center',
        },
        productBrand: {
          ...theme.typography.md,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
        },
        productModel: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
          marginTop: theme.spacing.xxs,
        },
        productFamily: {
          ...theme.typography.xs,
          color: theme.colors.textTertiary,
          marginTop: theme.spacing.xxs,
          letterSpacing: 0,
        },
        label: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textSecondary,
          marginBottom: theme.spacing.sm,
          marginTop: theme.spacing.md,
        },
        issueTypeGrid: {
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: theme.spacing.sm,
          marginBottom: theme.spacing.sm,
        },
        issueTypeButton: {
          paddingHorizontal: theme.spacing.md,
          paddingVertical: theme.spacing.sm,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          backgroundColor: 'transparent',
        },
        issueTypeButtonActive: {
          backgroundColor: theme.colors.accent,
          borderColor: theme.colors.accent,
        },
        issueTypeText: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
        },
        issueTypeTextActive: {
          color: theme.colors.backgroundPrimary,
        },
        input: {
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          paddingVertical: theme.spacing.md,
          paddingHorizontal: theme.spacing.lg,
          ...theme.typography.md,
          color: theme.colors.textPrimary,
        },
        textArea: {
          minHeight: 100,
          textAlignVertical: 'top',
        },
        priorityButtons: {
          flexDirection: 'row',
          gap: theme.spacing.sm,
        },
        priorityButton: {
          flex: 1,
          paddingVertical: theme.spacing.md,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          backgroundColor: 'transparent',
          alignItems: 'center',
        },
        priorityButtonActive: {
          backgroundColor: theme.colors.accent,
          borderColor: theme.colors.accent,
        },
        priorityText: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
        },
        priorityTextActive: {
          color: theme.colors.backgroundPrimary,
        },
        submitButton: {
          backgroundColor: theme.colors.accent,
          paddingVertical: theme.spacing.md,
          paddingHorizontal: theme.spacing.xl,
          borderRadius: theme.radius.radiusMd,
          alignItems: 'center',
          marginTop: theme.spacing.xxl,
        },
        submitButtonDisabled: {
          opacity: 0.5,
        },
        submitButtonText: {
          ...theme.typography.md,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.backgroundPrimary,
        },
        cancelButton: {
          backgroundColor: 'transparent',
          paddingVertical: theme.spacing.md,
          paddingHorizontal: theme.spacing.xl,
          borderRadius: theme.radius.radiusMd,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          alignItems: 'center',
          marginTop: theme.spacing.sm,
        },
        cancelButtonText: {
          ...theme.typography.md,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
        },
        errorText: {
          ...theme.typography.md,
          color: theme.colors.textSecondary,
        },
      }),
    [theme],
  );
  const { productId } = route.params;
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [product, setProduct] = useState<UserProductWithDetails | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  
  // Form state
  const [issueType, setIssueType] = useState<IssueType>('wrong_model');
  const [reportedField, setReportedField] = useState('');
  const [currentValue, setCurrentValue] = useState('');
  const [suggestedValue, setSuggestedValue] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<ReportPriority>('medium');

  useEffect(() => {
    loadProduct();
  }, [productId]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      const data = await getUserProductById(productId);
      if (data) {
        setProduct(data);
        
        // Pre-fill current values based on product data
        if (data.variant?.model?.name) {
          setCurrentValue(data.variant.model.name);
        }
        
        // Load first image if available
        if (data.images) {
          const url = await getFirstImageUrl(data.images);
          setImageUrl(url);
        }
      }
    } catch (error) {
      console.error('Error loading product:', error);
      Alert.alert('Error', 'Failed to load product details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!suggestedValue.trim() && !description.trim()) {
      Alert.alert('Required', 'Please provide either a suggested value or description');
      return;
    }

    try {
      setSubmitting(true);
      
      const result = await createIssueReport({
        user_product_id: productId,
        issue_type: issueType,
        reported_field: reportedField || undefined,
        current_value: currentValue || undefined,
        suggested_value: suggestedValue || undefined,
        description: description || undefined,
        priority,
      });

      if (result) {
        Alert.alert(
          'Report Submitted',
          'Thank you for reporting this issue. Our team will review it shortly.',
          [
            {
              text: 'OK',
              onPress: () => navigation.goBack(),
            },
          ]
        );
      } else {
        Alert.alert('Error', 'Failed to submit report. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting report:', error);
      Alert.alert('Error', 'Failed to submit report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color={theme.colors.accent} />
      </View>
    );
  }

  if (!product) {
    return (
      <View style={styles.centered}>
        <Text style={styles.errorText}>Product not found</Text>
      </View>
    );
  }

  const brandName = product.variant?.model?.family?.brand?.name || 'Unknown';
  const modelName = product.variant?.model?.name || 'Unknown';
  const familyName = product.variant?.model?.family?.name || '';

  return (
    <ScrollView style={styles.container}>
      <View style={styles.content}>
        <Text style={styles.title}>Report an Issue</Text>
        
        {/* Product Preview */}
        <View style={styles.productPreview}>
          {imageUrl && (
            <Image source={{ uri: imageUrl }} style={styles.productImage} />
          )}
          <View style={styles.productInfo}>
            <Text style={styles.productBrand}>{brandName}</Text>
            <Text style={styles.productModel}>{modelName}</Text>
            {familyName && <Text style={styles.productFamily}>{familyName}</Text>}
          </View>
        </View>

        {/* Issue Type */}
        <Text style={styles.label}>What's wrong?</Text>
        <View style={styles.issueTypeGrid}>
          {(Object.keys(ISSUE_TYPE_LABELS) as IssueType[]).map((type) => (
            <TouchableOpacity
              key={type}
              style={[
                styles.issueTypeButton,
                issueType === type && styles.issueTypeButtonActive,
              ]}
              onPress={() => {
                setIssueType(type);
                // Auto-fill reported field based on issue type
                if (type === 'wrong_brand') {
                  setReportedField('brand');
                  setCurrentValue(brandName);
                } else if (type === 'wrong_model') {
                  setReportedField('model');
                  setCurrentValue(modelName);
                } else if (type === 'wrong_family') {
                  setReportedField('family');
                  setCurrentValue(familyName);
                } else if (type === 'wrong_valuation') {
                  setReportedField('valuation');
                  setCurrentValue(product.valuation?.toString() || '');
                } else if (type === 'wrong_condition') {
                  setReportedField('condition');
                  setCurrentValue(product.condition || '');
                }
              }}
            >
              <Text
                style={[
                  styles.issueTypeText,
                  issueType === type && styles.issueTypeTextActive,
                ]}
              >
                {ISSUE_TYPE_LABELS[type]}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Current Value */}
        <Text style={styles.label}>What AI detected</Text>
        <TextInput
          style={styles.input}
          value={currentValue}
          onChangeText={setCurrentValue}
          placeholder="Current value from AI"
          placeholderTextColor={theme.colors.textTertiary}
        />

        {/* Suggested Value */}
        <Text style={styles.label}>What it should be</Text>
        <TextInput
          style={styles.input}
          value={suggestedValue}
          onChangeText={setSuggestedValue}
          placeholder="Your suggested correction"
          placeholderTextColor={theme.colors.textTertiary}
        />

        {/* Description */}
        <Text style={styles.label}>Additional details (optional)</Text>
        <TextInput
          style={[styles.input, styles.textArea]}
          value={description}
          onChangeText={setDescription}
          placeholder="Explain the issue or provide context..."
          placeholderTextColor={theme.colors.textTertiary}
          multiline
          numberOfLines={4}
          textAlignVertical="top"
        />

        {/* Priority */}
        <Text style={styles.label}>Priority</Text>
        <View style={styles.priorityButtons}>
          {(['low', 'medium', 'high'] as ReportPriority[]).map((p) => (
            <TouchableOpacity
              key={p}
              style={[
                styles.priorityButton,
                priority === p && styles.priorityButtonActive,
              ]}
              onPress={() => setPriority(p)}
            >
              <Text
                style={[
                  styles.priorityText,
                  priority === p && styles.priorityTextActive,
                ]}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Submit Button */}
        <TouchableOpacity
          style={[styles.submitButton, submitting && styles.submitButtonDisabled]}
          onPress={handleSubmit}
          disabled={submitting}
        >
          {submitting ? (
            <ActivityIndicator color={theme.colors.backgroundPrimary} />
          ) : (
            <Text style={styles.submitButtonText}>Submit Report</Text>
          )}
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.cancelButton}
          onPress={() => navigation.goBack()}
          disabled={submitting}
        >
          <Text style={styles.cancelButtonText}>Cancel</Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
}

